import {
  addBlock,
  changeBlock,
  getBlocksFieldname,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers/Blocks/Blocks';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { IMAGE } from '@plone/volto-slate/constants';
import { blockTagDeserializer } from '@plone/volto-slate/editor/deserialize';
import imageExtensions from 'image-extensions';
import isUrl from 'is-url';
import ReactDOM from 'react-dom';
import { Transforms } from 'slate';
import { v4 as uuid } from 'uuid';

import {
  parseSpreadsheetClipboard,
  readRichClipboardPayload,
} from '../../Table/tablePaste';

export const insertImage = (editor, url, { typeImg = IMAGE } = {}) => {
  const image = { type: typeImg, url, children: [{ text: '' }] };
  Transforms.insertNodes(editor, image);
};

export const isImageUrl = (url) => {
  if (!isUrl(url)) return false;

  const ext = new URL(url).pathname.split('.').pop();

  return imageExtensions.includes(ext);
};

const createTableBlockData = (grid) => ({
  '@type': 'slateTable',
  table: {
    hideHeaders: false,
    fixed: true,
    compact: false,
    basic: false,
    celled: true,
    inverted: false,
    striped: false,
    rows: grid.map((values, rowIndex) => ({
      key: uuid(),
      cells: values.map((value) => ({
        key: uuid(),
        type: rowIndex === 0 ? 'header' : 'data',
        value,
      })),
    })),
  },
});

const containsMixedTableContent = (html) => {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const tables = Array.from(document.body.querySelectorAll('table'));
  if (tables.length > 1) return true;
  if (tables.length !== 1) return false;

  const contentWithoutTable = document.body.cloneNode(true);
  contentWithoutTable
    .querySelectorAll('table, style, script, meta, link, title')
    .forEach((element) => element.remove());

  return Boolean(
    contentWithoutTable.textContent.replace(/\u00a0/g, ' ').trim(),
  );
};

const insertTableBlock = (editor, result) => {
  if (!result.handled) return false;

  const blockProps = editor.getBlockProps?.();
  if (!blockProps?.properties || !blockProps?.onChangeField) return false;

  const newBlock = createTableBlockData(result.grid);
  const [newBlockId, formData] = addBlock(
    blockProps.properties,
    'slateTable',
    blockProps.index + 1,
    null,
    blockProps.intl,
  );
  const blocksFieldname = getBlocksFieldname(formData);
  const updatedFormData = changeBlock(formData, newBlockId, {
    ...formData[blocksFieldname][newBlockId],
    ...newBlock,
  });
  const blocksLayoutFieldname = getBlocksLayoutFieldname(updatedFormData);

  ReactDOM.unstable_batchedUpdates(() => {
    blockProps.onChangeField(blocksFieldname, updatedFormData[blocksFieldname]);
    blockProps.onChangeField(
      blocksLayoutFieldname,
      updatedFormData[blocksLayoutFieldname],
    );
    blockProps.onSelectBlock?.(newBlockId);
  });
  return true;
};

export const onImageLoad = (editor, reader) => () => {
  const data = reader.result;

  // if (url) insertImage(editor, url);
  const fields = data.match(/^data:(.*);(.*),(.*)$/);
  const blockProps = editor.getBlockProps();
  const { block, uploadContent, pathname } = blockProps;

  // TODO: we need a way to get the uploaded image URL
  // This would be easier if we would have block transformers-based image
  // blocks
  const url = getBaseUrl(pathname);
  const uploadId = uuid();
  const uploadFileName = `clipboard-${uploadId}`;
  const uploadTitle = `Clipboard image`;
  const content = {
    '@type': 'Image',
    title: uploadTitle,
    image: {
      data: fields[3],
      encoding: fields[2],
      'content-type': fields[1],
      filename: uploadFileName,
    },
  };

  uploadContent(url, content, block);
};

export const withDeserializers = (editor) => {
  editor.htmlTagsToSlate = {
    ...editor.htmlTagsToSlate,

    // We don't want H1 tags when pasting, we rewrite them as H2
    H1: blockTagDeserializer('h2'),
  };

  const handleFiles = editor.dataTransferHandlers?.files || (() => true);
  const handleHtml = editor.dataTransferHandlers?.['text/html'];
  const handlePlainText = editor.dataTransferHandlers?.['text/plain'];

  editor.dataTransferHandlers = {
    ...editor.dataTransferHandlers,
    'text/html': (html, mimeType) => {
      const result = parseSpreadsheetClipboard({ html });
      if (result.handled && containsMixedTableContent(html) && handleHtml) {
        return handleHtml(html, mimeType);
      }
      return insertTableBlock(editor, result) || handleHtml?.(html, mimeType);
    },
    'text/plain': (text, mimeType) => {
      const result = parseSpreadsheetClipboard({ text });
      if (!result.handled) return handlePlainText?.(text, mimeType);

      if (typeof navigator !== 'undefined' && navigator.clipboard?.read) {
        readRichClipboardPayload(
          {
            types: ['text/plain'],
            html: '',
            text,
            formats: { 'text/plain': text },
          },
          navigator.clipboard,
        ).then((payload) => {
          if (
            payload.html &&
            containsMixedTableContent(payload.html) &&
            handleHtml
          ) {
            handleHtml(payload.html, 'text/html');
            return;
          }
          insertTableBlock(editor, parseSpreadsheetClipboard(payload));
        });
        return true;
      }

      return insertTableBlock(editor, result);
    },
    files: (files) => {
      const unprocessed = [];

      const blockProps = editor.getBlockProps ? editor.getBlockProps() : {};
      const { intl, openImageUploadModal } = blockProps || {};

      const images = [];

      for (const file of files) {
        if (!validateFileUploadSize(file, intl?.formatMessage)) return;
        const [mime] = file.type.split('/');

        if (mime === 'image' && openImageUploadModal) {
          images.push(file);
        } else {
          unprocessed.push(file);
        }
      }

      if (images.length && openImageUploadModal) {
        // Abre o modal com todas as imagens coletadas (drag-drop ou paste)
        openImageUploadModal(images);
      }

      // Deixar o restante seguir o fluxo padrão
      return handleFiles(unprocessed);
    },
  };

  return editor;
};
